'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Menu, X, PlusCircle, HelpCircle, LogOut, User } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

// Love Locals SVG logo - extracted from lovelocals.co.uk (color #3A2047)
const LoveLocalsLogo = ({ className = '' }: { className?: string }) => (
  <svg
    width="120"
    height="21"
    viewBox="0 0 400 69"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M307.962 68.5175L314.5 68.5L316.928 61.4672H330.014L332.5 68.5175H369.273V64.9923C369.354 64.9923 370.86 65.7777 372.37 66.4499C383.192 71.2694 396.721 68.7826 399.293 61.501C400.55 57.9421 399.838 53.733 399.384 52.4363C398.08 48.7161 394.383 45.7883 388.579 43.8777C384.419 42.5085 382.696 41.0214 384.666 39.3928C386.8 38.6713 389.133 38.8527 391.791 40.2012L394.344 41.4961L399.652 32.3261C397.549 30.916 395.217 29.5647 391.791 28.8009C385.336 27.3617 378.672 28.6924 375.126 32.2156C373.246 34.4412 372.019 36.0862 372.019 40.1791C372.019 46.0746 375.063 49.6091 382.696 52.5768C386.121 53.9084 387.968 55.1219 387.968 56.368C387.968 57.614 386.281 57.9421 384.697 57.9421C382.718 57.9421 378.299 56.7534 375.995 55.3861L374.415 54.4169L369.273 63.1123V57.707H355.706V29H343.568V63.5015L329.871 29H316.871L303.374 63.5015L302.323 66.0095L301.271 68.5175H307.962ZM323.5 43L326.5 51.1268H320.5L323.5 43Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M16.8776 0.637919C10.6742 2.31541 6.23283 5.98861 3.57865 11.6373C1.76666 15.4938 1.34976 21.0635 2.52239 25.7637C3.60529 30.1062 7.82843 38.5811 11.9562 44.6951C15.461 49.8869 21.0456 57.2022 21.499 57.1957C21.921 57.1896 27.8318 49.5729 30.9501 45.0166C36.5249 36.8711 40.0456 29.4007 41.1215 23.4347C42.2394 17.2352 40.1091 10.5572 35.5514 5.97356C30.465 0.858357 23.4615 -1.14298 16.8776 0.637919ZM14.0211 13.9592C11.9179 16.6856 12.2703 19.7051 17.1506 24.842C19.0962 26.8903 21.4733 28.5659 21.4733 28.5659C24.9882 25.5853 32.5178 20.4577 29.6733 14.926C28.2637 12.1849 25.079 11.7327 22.8651 13.9592L21.499 15.333L20.1329 13.9592C18.3564 12.1726 15.6592 12.3118 14.0211 13.9592ZM96.6393 28.8268C90.6589 30.2848 85.7393 34.2701 83.0805 39.8116C80.7546 44.6584 80.6452 51.0161 82.4678 56.0399C84.7373 62.2991 90.1985 67.0806 96.6515 68.4587C104.338 70.1 111.8 67.9605 116.751 62.6958C119.755 59.5006 121.48 55.8857 122.002 51.6856C123.356 40.7864 117.473 31.6394 107.397 28.9791C103.518 27.9544 100.398 27.9102 96.6393 28.8268ZM56.318 68.5175H82.0233V58.1771H68.4696V29H56.318V68.5175ZM120.46 29C120.46 29 130.314 57.2542 133.901 68.5391H146.988L160.074 29.506V49.9517V68.5175H187.181V58.6471H172.226V53.4769H187.181V43.6065H172.226V38.9063H187.181V29H147.455L140.444 50.6568L133.547 29H120.46ZM201.67 68.5175H227.375V57.707H213.821V29H201.67V68.5175ZM99.3884 39.5864C95.8392 40.8701 93.5033 44.3388 93.4841 48.3542C93.4528 54.933 98.4275 59.1594 104.18 57.4415C107.357 56.4925 109.313 54.0183 110.029 50.0439C110.41 47.9269 109.364 43.8598 107.983 42.0949C106.138 39.7359 102.174 38.5792 99.3884 39.5864ZM0 68.7291H42.998V59.6923H0V68.7291Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M253.142 68.4761C260.408 67.1248 265.81 62.1417 268.082 54.6909L269.074 51.1268L270.114 54.627C271.899 60.0957 275.233 63.9461 280.528 66.6553C285.585 69.2418 293.249 69.6267 299.028 67.584C300.617 67.0221 301.757 66.31 302.323 66.0095L303.374 63.5015L298.467 55.357C293.835 58.152 287.04 58.1381 283.457 53.6823C282.576 52.0668 282.109 50.8918 282.109 48.3067C282.343 43.5698 285.587 40.7864 289.025 40.213C292.272 39.6714 295.469 40.4066 298.467 41.7264C299.834 39.182 303.374 31.9543 303.374 31.9543C294.4 27.6259 288.81 27.9045 283.278 29.271C276.548 30.933 272.023 36.1124 270.114 41.7264C269.644 43.1084 269.199 46.0779 269.074 47.3666C267.828 39.1389 262.357 31.1385 254.099 28.9532C241.007 25.4889 228.129 33.9711 228.128 48.7767C228.127 62.2154 239.818 70.953 253.142 68.4761ZM241.769 54.2778C240.006 51.5996 239.756 46.8712 241.216 43.8377C243.87 38.3254 250.783 37.9393 254.667 42.3877C257.15 45.2318 257.291 51.0944 254.957 54.4813C251.831 59.0184 244.817 58.9103 241.769 54.2778Z"
      fill="currentColor"
    />
  </svg>
);

const navLinks = [
  { label: 'Find a Trade', href: '/provider-directory', key: 'nav-find' },
  { label: 'Community', href: '#community', key: 'nav-community' },
  { label: 'For Providers', href: '#providers', key: 'nav-providers' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, loading, signOut } = useAuth();
  const router = useRouter();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
      router.replace('/homepage');
      router.refresh();
    } catch {
      // ignore
    }
  };

  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Account';
  const isProvider = user?.user_metadata?.account_type === 'provider';
  const isHomeowner = !isProvider;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100'
          : 'bg-white/95 backdrop-blur-md border-b border-gray-100'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link href="/homepage" className="flex items-center gap-2 group">
            <LoveLocalsLogo className="text-love-plum-light h-7 w-auto" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks?.map((link) => (
              <Link
                key={link?.key}
                href={link?.href}
                className="px-3.5 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-love-plum-light hover:bg-purple-50 transition-all duration-150"
              >
                {link?.label}
              </Link>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="hidden md:flex items-center gap-2">
            <Link
              href="/post-job"
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-md text-sm font-medium border border-love-mint text-love-plum-light hover:bg-love-mint/10 transition-all duration-200"
            >
              <PlusCircle size={15} />
              Post a Job
            </Link>
            <Link
              href="#"
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-md text-sm font-medium border border-love-coral text-love-coral hover:bg-love-coral/10 transition-all duration-200"
            >
              <HelpCircle size={15} />
              Ask a Professional
            </Link>

            {!loading && user ? (
              <>
                {/* Authenticated: show user name + dashboard + sign out */}
                <Link
                  href={isProvider ? '/provider/dashboard' : '/homeowner/dashboard'}
                  className="flex items-center gap-1.5 px-3.5 py-2 rounded-md text-sm font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all duration-200"
                >
                  <User size={15} />
                  <span className="max-w-[100px] truncate">{displayName}</span>
                </Link>
                {isProvider && (
                  <Link
                    href="/provider/dashboard"
                    className="flex items-center gap-1.5 px-3.5 py-2 rounded-md text-sm font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all duration-200"
                  >
                    Dashboard
                  </Link>
                )}
                {isHomeowner && (
                  <Link
                    href="/homeowner/dashboard"
                    className="flex items-center gap-1.5 px-3.5 py-2 rounded-md text-sm font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all duration-200"
                  >
                    My Dashboard
                  </Link>
                )}
                <button
                  onClick={handleSignOut}
                  className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-100 transition-all duration-150"
                >
                  <LogOut size={15} />
                  Log out
                </button>
              </>
            ) : (
              <>
                {/* Unauthenticated */}
                <Link
                  href="/login"
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-all duration-150"
                >
                  Log in
                </Link>
                <Link
                  href="/provider/dashboard"
                  className="flex items-center gap-1.5 px-3.5 py-2 rounded-md text-sm font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all duration-200"
                >
                  Dashboard
                </Link>
                <Link
                  href="/provider/signup"
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-love-coral text-white hover:bg-orange-600 shadow-md transition-all duration-150 active:scale-95"
                >
                  Join as a Pro
                </Link>
              </>
            )}
          </div>

          {/* Mobile: Join + hamburger */}
          <div className="md:hidden flex items-center gap-2">
            {!loading && user ? (
              <button
                onClick={handleSignOut}
                className="px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-100 transition-all"
              >
                <LogOut size={18} />
              </button>
            ) : (
              <Link
                href="/login?mode=signup&type=provider"
                className="px-3 py-2 rounded-md text-sm font-semibold bg-love-coral text-white hover:bg-orange-600 shadow-md transition-all"
              >
                Join
              </Link>
            )}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="px-4 py-3 space-y-1">
            {navLinks?.map((link) => (
              <Link
                key={`mobile-${link?.key}`}
                href={link?.href}
                onClick={() => setMobileOpen(false)}
                className="block px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-purple-50 hover:text-love-plum-light transition-colors"
              >
                {link?.label}
              </Link>
            ))}
            <div className="pt-2 border-t border-gray-100 flex flex-col gap-2">
              {!loading && user ? (
                <>
                  <div className="px-3 py-2 text-sm text-gray-500">
                    Signed in as <span className="font-medium text-gray-800">{displayName}</span>
                  </div>
                  {isProvider && (
                    <Link
                      href="/provider/dashboard"
                      onClick={() => setMobileOpen(false)}
                      className="block px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Provider Dashboard
                    </Link>
                  )}
                  {isHomeowner && (
                    <Link
                      href="/homeowner/dashboard"
                      onClick={() => setMobileOpen(false)}
                      className="block px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      My Dashboard
                    </Link>
                  )}
                  <button
                    onClick={() => { setMobileOpen(false); handleSignOut(); }}
                    className="block w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Log out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    href="/login"
                    onClick={() => setMobileOpen(false)}
                    className="block px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Log in
                  </Link>
                  <Link
                    href="/provider/dashboard"
                    onClick={() => setMobileOpen(false)}
                    className="block px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Provider Dashboard
                  </Link>
                  <Link
                    href="/provider/signup"
                    onClick={() => setMobileOpen(false)}
                    className="block px-4 py-2.5 rounded-lg text-sm font-semibold bg-love-coral text-white text-center hover:bg-orange-600 transition-colors"
                  >
                    Join as a Pro
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}